import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';

const KaskoSeo = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Insurance",
    "name": "Kasko Sigortası",
    "description": "En uygun kasko sigortası tekliflerini anında karşılaştırın. 20'den fazla sigorta şirketinden online kasko teklifi alın.",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "980"
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceSpecification": {
        "@type": "PriceSpecification",
        "price": "0",
        "priceCurrency": "TRY"
      }
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Kasko sigortası nedir?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Kasko sigortası, aracınızın uğrayabileceği hasarlara karşı güvence sağlayan bir sigorta türüdür. Çarpma, çarpışma, yanma, çalınma gibi risklere karşı aracınızı korur."
        }
      },
      {
        "@type": "Question",
        "name": "Kasko sigortası neleri kapsar?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Kasko sigortası; çarpma, çarpışma, yanma, çalınma, sel, deprem, terör gibi risklere karşı koruma sağlar. Ek teminatlarla cam, far, anahtar, mini onarım hasarları da kapsama dahil edilebilir."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>2025 Kasko Sigortası Fiyatlarını Karşılaştır | SigortamOlsun</title>
        <meta name="description" content="En uygun kasko sigortası fiyatları için hemen online teklif alın! 20+ sigorta şirketinin tekliflerini anında karşılaştırın, %45'e varan indirimlerden yararlanın." />
        <meta name="keywords" content="kasko, kasko sigortası, kasko fiyatları, kasko teklifi, kasko hesaplama" />
        <link rel="canonical" href="https://sigortamolsun.com/kasko" />
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="max-w-5xl mx-auto px-4 py-8 md:py-12">
        {/* Neden SigortamOlsun */}
        <section className="mb-12 bg-gradient-to-br from-blue-50 to-purple-50 -mx-4 p-8">
          <h2 className="text-2xl font-bold mb-6 text-center">Neden SigortamOlsun.com ?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center gap-4 mb-3">
                <span role="img" aria-label="kıyaslama" className="text-3xl">🔍</span>
                <h3 className="font-semibold">Fiyat Karşılaştırma</h3>
              </div>
              <p className="text-gray-600 text-sm">20'den fazla sigorta şirketinin kasko tekliflerini anında karşılaştırın.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center gap-4 mb-3">
                <span role="img" aria-label="zaman" className="text-3xl">⚡️</span>
                <h3 className="font-semibold">Hızlı İşlem</h3>
              </div>
              <p className="text-gray-600 text-sm">2 dakikada kasko tekliflerinizi alın, online ödeyin.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-center gap-4 mb-3">
                <span role="img" aria-label="tasarruf" className="text-3xl">💰</span>
                <h3 className="font-semibold">Tasarruf</h3>
              </div>
              <p className="text-gray-600 text-sm">En uygun fiyatlarla kasko sigortanızı yaptırın.</p>
            </div>
          </div>
        </section>

        {/* Kasko Bilgilendirme */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Kasko Sigortası</h2>
          <div className="prose max-w-none space-y-8">
            <div>
              <h3 className="text-xl font-semibold mb-3">Kasko Sigortası Nedir?</h3>
              <p>
                Kasko sigortası, aracınızın karşılaşabileceği çarpma, çarpışma, yanma, çalınma gibi risklere karşı güvence sağlayan bir sigorta türüdür. 
                Zorunlu trafik sigortasından farklı olarak kendi aracınızın uğrayacağı hasarları teminat altına alır. İsteğe bağlı ek teminatlarla 
                güvencenizi genişletebilirsiniz.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold mb-3">Kasko Sigortası Teminatları</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li>Çarpma, Çarpışma, Devrilme</li>
                <li>Yanma ve Patlama</li>
                <li>Çalınma veya Çalınmaya Teşebbüs</li>
                <li>Doğal Afetler (Sel, Deprem, Fırtına vb.)</li>
                <li>Cam ve Far Hasarları</li>
                <li>İhtiyari Mali Mesuliyet (İ.M.M)</li>
                <li>Mini Onarım Hasarları</li>
                <li>İkame Araç</li>
                <li>Yol Yardım</li>
              </ul>
            </div>
          </div>
        </section>

        {/* İsteğe Bağlı Ek Teminatlar */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">İsteğe Bağlı Ek Teminatlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">İMM (İhtiyari Mali Mesuliyet)</h3>
              <p className="text-gray-600 text-sm">
                Trafik sigortası limitlerinin üzerindeki hasarları karşılar.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Yurtdışı Teminatı</h3>
              <p className="text-gray-600 text-sm">
                Yurtdışında aracınıza gelebilecek hasarları teminat altına alır.
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Kişisel Eşya</h3>
              <p className="text-gray-600 text-sm">
                Araç içindeki kişisel eşyalarınızın çalınması veya hasarını karşılar.
              </p>
            </div>
          </div>
        </section>

        {/* SSS */}
        <section className="mb-12 bg-gradient-to-br from-blue-50 to-purple-50 -mx-4 p-8">
          <h2 className="text-2xl font-bold mb-6">Kasko Hakkında Sıkça Sorulan Sorular</h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Kasko Fiyatları Neye Göre Belirlenir?</h3>
              <p className="text-gray-600 text-sm">
                Aracın markası, modeli, yaşı, motor hacmi, kullanım amacı, sürücü yaşı ve deneyimi, hasarsızlık indirimi gibi faktörler kasko fiyatını etkiler.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">Mini Onarım Paketi Nedir?</h3>
              <p className="text-gray-600 text-sm">
                Küçük çizik, göçük, döşeme tamiri gibi hasarların muafiyet uygulanmadan karşılanmasını sağlayan ek teminattır.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="font-semibold mb-3">İkame Araç Teminatı Nedir?</h3>
              <p className="text-gray-600 text-sm">
                Aracınız hasarlı olduğunda veya çalındığında size geçici bir araç tahsis edilmesini sağlayan teminattır.
              </p>
            </div>
          </div>
        </section>

        {/* Müşteri Yorumları */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Müşteri Yorumları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-semibold">MS</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold">Mehmet S.</h3>
                  <div className="flex text-yellow-400 mb-2">★★★★★</div>
                  <p className="text-gray-600 text-sm">
                    "En uygun kasko teklifini SigortamOlsun'da buldum. İşlemler çok hızlı ve kolay."
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-semibold">EA</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold">Esra A.</h3>
                  <div className="flex text-yellow-400 mb-2">★★★★★</div>
                  <p className="text-gray-600 text-sm">
                    "Mini onarım paketi sayesinde küçük hasarları çok rahat hallettim."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* İlgili Sigortalar */}
        <section className="mb-12 bg-gradient-to-br from-blue-50 to-purple-50 -mx-4 p-8">
          <h2 className="text-2xl font-bold mb-6">İlgili Sigortalar</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Link to="/trafik-sigortasi" className="block bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
              <h3 className="font-semibold mb-2">Trafik Sigortası</h3>
              <p className="text-gray-600 text-sm">Zorunlu trafik sigortanızı hemen yaptırın.</p>
            </Link>
            <Link to="/ihtiyari-mali-mesuliyet" className="block bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition">
              <h3 className="font-semibold mb-2">İMM Sigortası</h3>
              <p className="text-gray-600 text-sm">Trafik sigortası teminatlarınızı genişletin.</p>
            </Link>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default KaskoSeo;